import React, { useCallback, useState } from 'react';

import { Tooltip } from '@ovpn-ui/core';

import { CopyCellWrapper, CopyIcon, StypedCopyIdIcon } from './CopyCell.styles';

type CopyCellProps = {
    children?: React.ReactNode;
    copyValue?: string;
    copyText?: string;
    copiedText?: string;
    iconVerticalOffset?: number;
    onCopy?: any;
    inline?: boolean;
    visible?: boolean;
    color?: string;
    placement?: React.ComponentProps<typeof Tooltip>['placement'];
    component?: any;
    icon?: any;

    /**
     * Will trigger copy only on icon click, not on wrapped children.
     */
    onlyIconClick?: boolean;
} & React.ComponentProps<typeof CopyCellWrapper>;

export function CopyCell({
    component,
    copyValue,
    children,
    placement = 'top',
    inline,
    visible,
    onCopy,
    color,
    copyText = 'Copy',
    copiedText = 'Copied!',
    icon,
    // negative vertical offset of the svg icon can be used to handle its different spacing in Chrome & Firefox (important when icon influences text line height)
    iconVerticalOffset,
    onlyIconClick,
    ...props
}: CopyCellProps) {
    const [isCopied, setCopied] = useState(false);

    const IconComponent = icon ?? CopyIcon;

    const handleCopyProxy = useCallback(
        e => {
            setCopied(true);
            navigator.clipboard.writeText(copyValue!);

            if (onCopy) {
                onCopy(e);
            }
        },
        [copyValue, onCopy],
    );

    const handleMouseLeave = useCallback(() => {
        setCopied(false);
    }, []);

    if (!copyValue && typeof copyValue !== 'number') {
        return children as JSX.Element;
    }

    return (
        <CopyCellWrapper
            as={component}
            $inline={inline}
            $onlyIconClick={onlyIconClick}
            onClick={onlyIconClick ? undefined : handleCopyProxy}
            onMouseLeave={handleMouseLeave}
            {...props}
        >
            {children}
            <Tooltip
                open={isCopied ? true : undefined}
                leaveDelay={0}
                placement={placement}
                trigger={
                    <IconComponent
                        $visible={visible}
                        $copied={isCopied}
                        $mY={iconVerticalOffset}
                        $color={color}
                        onClick={onlyIconClick ? handleCopyProxy : undefined}
                    />
                }
            >
                {isCopied ? copiedText : copyText}
            </Tooltip>
        </CopyCellWrapper>
    );
}

export function CopyId({
    id,
    children,
}: {
    id: CopyCellProps['copyValue'];
    children?: CopyCellProps['children'];
}) {
    return (
        <CopyCell
            placement="bottom"
            copyText="Copy ID"
            copyValue={id}
            visible
            icon={StypedCopyIdIcon}
            onlyIconClick
        >
            {children}
        </CopyCell>
    );
}
